import { APIModule } from '@modules/api/src/APIModule';
import { Resolution } from '@modules/babylonjs';
import {
  BABYLONJS_MODULE_OPTIONS_TOKEN,
  BabylonjsModule,
  BabylonjsModuleOptions,
  getQualitySettings,
} from '@modules/babylonjs/src/BabylonjsModule';
import { checkDeviceTier, DeviceInfo } from '@modules/babylonjs/src/DeviceInfo';
// import { CesiumModule } from '@modules/cesium';
import { Layers, ZedLoadError } from '@modules/core';
import { ModulesApi } from '@modules/core/src/injectable/Module';
import { useErrorBoundary, ZedComponent } from '@modules/core/src/react';
import { Zed } from '@modules/core/src/Zed';
import { getDeviceInfo, ProfileDebugInfo } from '@modules/standard';
import { InspectorComponent } from '@modules/standard/src/react/Inspector';
import { StandardModule } from '@modules/standard/src/StandardModule';
import { zams } from '@zams/logger/zed/generated/loggerMap';
import { forwardRef, memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useScreen } from '@modules/tourdanji/src/react/hooks/useScreen';

import { onLoadCompleted } from '../observable/TourDanjiObservable';
import {
  getTourDanjiMetaData,
  getTourDanjiZamsParams,
  setTourDanjiMetaData,
  TOURDANJI_MODULE_OPTIONS_TOKEN,
  TourDanjiModule,
  TourDanjiOptions,
} from '../TourDanjiModule';
import FPS from '../utiles/FPS';

import watermarkZB from './assets/watermark.png';
import watermarkHG from './assets/watermark_hg.svg';
import { DanjiInfoIcon } from './DanjiInfoIcon';
import { DaylightPanel } from './DaylightPanel';
import { DebugPanel } from './DebugPanel';
import { DongPanel } from './DongPanel';
import { FacilityView } from './FacilityView';
import { GisComplexPanel } from './GisComplexPanel';
import { InteriorTourPanel } from './InteriorTourPanel';
import { InteriorViewDaylightPanel } from './InteriorViewDaylightPanel';
import { LoadingPanel } from './LoadingPanel';
import { RoomTypeFilter } from './RoomTypeFilter';
import styles from './styles/DanjiView.module.css';
import { SunPositionView } from './SunPositionView';
import { ToastContainer } from './ToastContainer';
import { TreePanel } from './TreePanel';
import { UniversalTutorialModal } from './TutorialModal';

export type DanjiProps = {
  danjiId: string;
  appVersion?: string | undefined;
  stage?: string | undefined;
  deviceInfo?: DeviceInfo | undefined;
  babylonjsOptions: BabylonjsModuleOptions;
  danjiOptions?: TourDanjiOptions | undefined;
  children?: ReactNode;
  useCesium?: boolean;
};

// https://bugs.webkit.org/show_bug.cgi?id=219780
// https://forums.developer.apple.com/forums/thread/668999
// https://forum.babylonjs.com/t/babylon-resizing-causing-ios-webkit-jetsam-memory-leak-event/45344
const supporteIOSVersion = 15;

const _DanjiViewComponent = forwardRef(function _DanjiViewComponent(
  {
    danjiId: danjiId,
    stage: stage,
    deviceInfo: deviceInfo,
    babylonjsOptions: babylonjsOptions,
    danjiOptions: danjiOptions,
    children /*useCesium */,
    appVersion,
  }: DanjiProps,
  _ref
) {
  const [showErrorBoundary] = useErrorBoundary();
  const [zedDeviceInfo] = useState(getDeviceInfo());
  const [unsupported] = useState(zedDeviceInfo.os === 'iOS' && zedDeviceInfo.md.version('iOS') < supporteIOSVersion);
  const [modules] = useState([APIModule, StandardModule, TourDanjiModule /*CesiumModule*/]);
  const [values] = useState({
    [BABYLONJS_MODULE_OPTIONS_TOKEN]: {
      ...babylonjsOptions,
      qualitySettings: {
        ...babylonjsOptions.qualitySettings,
        resolution: zedDeviceInfo.isMobileOrTablet ? Resolution.p720 : babylonjsOptions.qualitySettings.resolution,
      },
      ...({
        deviceInfo: deviceInfo,
      } as Partial<BabylonjsModuleOptions>),
    },
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: danjiOptions ?? {
      itemId: undefined,
      itemDongId: undefined,
      itemHoId: undefined,
      disableDecal: undefined,
      disableFacility: undefined,
      disableTransparent: undefined,
      season: undefined,
      areaHoId: undefined,
    },
    zed: undefined,
    tourDanjiMeta: undefined,
  });
  const [zed, setZed] =
    useState<Zed<ModulesApi<[BabylonjsModule, APIModule, StandardModule, TourDanjiModule /*CesiumModule*/]>>>();
  const [loadingTime, setLoadingTime] = useState<number | undefined>(undefined);
  const [profileDebugInfo, setProfileDebugInfo] = useState<ProfileDebugInfo | undefined>(undefined);
  const [watermark] = useState(danjiOptions?.domain === 'hogangnono' ? watermarkHG : watermarkZB);

  const [lastTapTime, setLastTapTime] = useState(0);

  useEffect(() => {
    const qualitySettings = getQualitySettings();
    qualitySettings.resolution = values[BABYLONJS_MODULE_OPTIONS_TOKEN].qualitySettings.resolution;
  }, [values[BABYLONJS_MODULE_OPTIONS_TOKEN].qualitySettings.resolution]);

  useEffect(() => {
    if (!zed || !loadingTime) return;

    const metaData = getTourDanjiMetaData();
    if (!metaData) return;

    zams.z135(getTourDanjiZamsParams()).send();

    FPS.reset(zed.api.scene);

    let fpsCheck: NodeJS.Timeout;
    const intervalId = setInterval(() => {
      if (FPS.measure() === -1) {
        FPS.reset(zed.api.scene);
      } else {
        fpsCheck = setTimeout(() => {
          const fps = FPS.measure().toString();
          zams
            .z134()
            .setAllParams({
              screen_name: metaData.screen_name,
              ...(metaData.deal_type && { deal_type: metaData.deal_type }),
              ...(metaData.item_type && { item_type: metaData.item_type }),
              ...(metaData.ho_id && { ho_id: metaData.ho_id }),
              area_danji_id: metaData.danjiId,
              dong: metaData.dong,
              ho: metaData.ho,
              item_id: metaData.itemId,
              status_value: fps,
              status: metaData.deviceTier ?? '',
              item_name: metaData.gpuModel ?? '',
              category_count: metaData.loadingTime.toString(),
            })
            .send();
        }, 20000);
        clearInterval(intervalId);
      }
    }, 1000);
    return () => {
      clearInterval(intervalId);
      if (fpsCheck) clearTimeout(fpsCheck);
    };
  }, [zed, loadingTime]);

  useEffect(() => {
    if (!zed) return;
    (async () => {
      setTourDanjiMetaData({ ...(await zed.api.loadDanji(danjiId)) });
      const metaData = getTourDanjiMetaData();

      setLoadingTime(metaData.loadingTime);
      setProfileDebugInfo(zed.api.getPerformanceInfo());

      if (danjiOptions?.areaHoId) {
        try {
          zed.api.createUnitImmediately(danjiOptions.areaHoId);
          await zed.api.enterUnitById(danjiOptions.areaHoId, true);
        } catch (e) {
          console.error(e);
        }
      }

      onLoadCompleted.notifyObservers();
      // if (useCesium === true) {
      //   const cesium = zed.api.cesium;
      //   await cesium.initCesium();
      //   const opt = {lat: 37.5449806, lng: 127.013839, h: 10, northRadian: 0}; // 옥수리버젠
      //   cesium.initCesiumCamera(opt);
      // }
    })().catch((e) =>
      showErrorBoundary(
        new ZedLoadError('3D단지투어를 생성하는 중 오류가 발생하였습니다.', {
          cause: e,
        })
      )
    );

    return () => {
      zed?.dispose();
    };
  }, [zed]);

  // 터치/클릭 처리 (왼쪽 상단 3번 연속 탭)
  const handleTap = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const event = e as MouseEvent | TouchEvent;
      const touch = 'touches' in event ? event.touches[0] : event;
      if (!touch) return;
      const clientX = touch.clientX;
      const clientY = touch.clientY;
      const windowHeight = window.innerHeight;

      // 우측 하단 영역 체크 (100x100 픽셀)
      if (clientX < 100 && clientY > windowHeight - 100) {
        const now = Date.now();

        // 연속 탭 시간 간격 체크 (500ms 이내)
        // Note: tapCount logic removed as it was unused

        setLastTapTime(now);
      }
    },
    [lastTapTime]
  );

  useEffect(() => {
    window.addEventListener('mousedown', handleTap);
    window.addEventListener('touchstart', handleTap);
    return () => {
      window.removeEventListener('mousedown', handleTap);
      window.removeEventListener('touchstart', handleTap);
    };
  }, [handleTap]);

  const Component = useMemo(
    () => (
      <ZedComponent
        engine={BabylonjsModule}
        values={values}
        modules={modules}
        ref={setZed}
        className={styles.danjiView}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          zIndex: Layers.BABYLON_CANVAS,
        }}>
        <SunPositionView />
        <FacilityView />
        <GisComplexPanel />
        <DongPanel />
        <InteriorTourPanel />
        <DaylightPanel />
        <InteriorViewDaylightPanel />
        <TreePanel />
        <RoomTypeFilter />
        {/* <ItemHoPanel /> */}
        {children}
        <img src={watermark} className={styles.watermark} />
        <DanjiInfoIcon />
        <ToastContainer />
        <LoadingPanel />
        <UniversalTutorialModal />
        {stage !== 'prod' && <InspectorComponent />}
      </ZedComponent>
    ),
    []
  );

  return (
    <div>
      {unsupported ? (
        <LoadingPanel messages={[`3D단지투어는 iOS15 이상에서 사용할 수 있습니다.\nOS를 업데이트해주세요.`]} />
      ) : (
        Component
      )}
      {stage !== 'prod' && (
        <DebugPanel
          danjiId={danjiId}
          appVersion={appVersion}
          deviceInfo={deviceInfo}
          danjiOptions={danjiOptions}
          loadingTime={loadingTime}
          scene={zed?.api.scene}
          profileDebugInfo={profileDebugInfo}
          tier={deviceInfo ? checkDeviceTier(deviceInfo.modelNumber, deviceInfo.deviceMemory) : undefined}
          getEntityInfo={zed?.api.getEntityInfo}
        />
      )}
    </div>
  );
});

export const DanjiView = memo(_DanjiViewComponent);
